/* eslint-disable @typescript-eslint/member-ordering */
import {
  Component,
  ViewChild,
  OnDestroy,
  TemplateRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  inject,
  LOCALE_ID,
} from "@angular/core";
import { TaskViewModel } from "../view-models/TaskViewModel";
import { ResourceViewModel } from "../view-models/ResourceViewModel";
import { Router, ActivatedRoute } from "@angular/router";
import { EnumNameValue } from "../../models/EnumNameValue";
import { TaskInsertComponent } from "./task-insert/task-insert.component";
import {
  TasksClientProvider,
  TaskStatusMetaDto,
  TasksExportCsvCommand,
  FileResponse,
  TasksDeleteMultiCommand,
  TaskUpdateCommand,
  TaskStatus,
  TaskInsertDto,
  ArchivedFilter,
  AppointmentArrangement,
  TaskCategoriesClientProvider,
  TaskCategoryDto,
  TaskDetailDto,
} from "src/app/web-api/web-api";
import { YesNoPipe } from "../../pipes/yes-no.pipe";
import { PlanungApiTaskImportCsvComponent } from "./import/task-import-csv.component";
import { FileHelper } from "src/app/helpers";
import { AlertService, MasterDataService } from "src/app/services";
import {
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from "rxjs/operators";
import { EMPTY, forkJoin, Observable, of, Subject } from "rxjs";
import { UserService } from "src/app/services/user.service";
import { CstNavigationService } from "src/app/services/cst-navigation.service";
import { FormControl } from "@angular/forms";
import {
  CstFilterBuilder,
  CstFilterFormats,
  ICstFilterGroup,
} from "src/app/common-components/filter";
import { CustomValidators } from "src/app/common-components/form-controls/custom-validators";
import { CstDisposableContainer } from "src/app/core";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import { AnlageSearchComponent } from "src/app/anlage-api/components/search/anlage-search.component";
import { ICstFilterItem } from "src/app/common-components/filter/models/cst-filter-item";
import { CstEnumsGeneralService } from "src/app/services/enums/enums-general.service";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { AnlageViewModel } from "../view-models";
import { PermissionService } from "src/app/services/permission.service";
import { CstDateTimeService } from "src/app/services/cst-date-time.service";
import { MaterialContextMenuComponent } from "src/app/common-components/material-context-menu/material-context-menu.component";
import { CstListViewComponent } from "src/app/common-components/list-view/list-view.component";
import { MatSort } from "@angular/material/sort";
import {
  CstListViewDataSource,
  ICstListViewColumn,
  ListColumnDisplay,
} from "src/app/common-components/list-view/models";
import { SelectionModel } from "@angular/cdk/collections";
import { ExcelService } from "src/app/services/excel.service";
import { CstFilterBarComponent } from "src/app/common-components/filter/components/cst-filter-bar.component";
import { BFF_IsSaasInstance } from "src/app/injection-tokens/backend-feature-flags";
import { CstFilterQueryFunctions } from "src/app/common-components/filter/models/cst-filter-queryfunctions";

interface ITaskListFilter {
  dateRangeStart?: Date;
  dateRangeEnd?: Date;
  dateRangesFilterOnlyToDoDate?: boolean;
  appointmentArrangements: AppointmentArrangement[];
  categories: string[];
  states: TaskStatus;
  anlageNr?: string[];
  taskNo?: string;
  taskText?: string;
  claimByResourceIds: string[];
  resourceID?: string[];
  anlageGroup?: string[];
  onlyWithUnreadNotes: boolean;
  workLocation: string;
}

class TaskStatesForMultiSelect {
  id: number;
  label: string;
  callback: () => any;

  constructor(_id: number, _text: string, _click: any) {
    this.id = _id;
    this.label = _text;
    this.callback = _click;
  }
}

enum IconType {
  FontAwesome,
  Material,
}

@Component({
  selector: "app-planung-api-tasks",
  templateUrl: "./planung-api-tasks.component.html",
  styleUrls: ["./planung-api-tasks.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PlanungApiTasksComponent implements OnDestroy {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _userService = inject(UserService);
  private readonly _tasksClient = inject(TasksClientProvider);
  private readonly _md = inject(MasterDataService);
  private readonly _nav = inject(CstNavigationService);
  private readonly _filterBuilder = inject(CstFilterBuilder);
  private readonly _popupService = inject(CstResponsivePopupService);
  private readonly _enumService = inject(CstEnumsGeneralService);
  private readonly _categoriesClient = inject(TaskCategoriesClientProvider);
  private readonly _dialog = inject(CstDialogsService);
  private readonly _alrt = inject(AlertService);
  private readonly _cd = inject(ChangeDetectorRef);
  private readonly _perm = inject(PermissionService);
  private readonly _dts = inject(CstDateTimeService);
  private readonly _excel = inject(ExcelService);
  private readonly _locale = inject(LOCALE_ID);
  private readonly _isSaasInstance = inject(BFF_IsSaasInstance);
  private readonly _yesNoPipe = inject(YesNoPipe);

  //#region TABLE TEMPLATES
  @ViewChild("notificationIcons", { read: TemplateRef, static: true })
  set notificationIconsRef(temp: TemplateRef<any>) {
    this._setColumnTemplate(temp, "appointmentArrangement");
  }

  @ViewChild("stateName", { read: TemplateRef, static: true }) set stateNameRef(
    temp: TemplateRef<any>,
  ) {
    this._setColumnTemplate(temp, "stateName");
  }

  @ViewChild("plannedFor", { read: TemplateRef, static: true })
  set plannedForRef(temp: TemplateRef<any>) {
    this._setColumnTemplate(temp, "plannedFor");
  }

  private _setColumnTemplate = (template: TemplateRef<any>, field: string) => {
    const col = this.columns.find((c) => c.field === field);

    if (col && template) {
      col.template = template;
      return col;
    }

    return undefined;
  };
  //#endregion

  //#region TABLE SETUP
  @ViewChild(CstListViewComponent) private _tableRef!: CstListViewComponent;
  @ViewChild(MatSort) sort!: MatSort;

  public cstDataSource: CstListViewDataSource<TaskViewModel> | null = null;
  public columns: ICstListViewColumn<TaskViewModel>[] = this._buildColumns(
    ListColumnDisplay.Web,
  );

  private get _selection(): SelectionModel<TaskViewModel> {
    return this._tableRef.selection;
  }
  private get _selected() {
    return this._selection.selected;
  }
  private get _selectedCount() {
    return this._selected.length;
  }

  public canDelete(item: TaskViewModel | null = null) {
    return (
      !this.isLoading &&
      this._perm.UserCan_PlanungAPI_TaskArchive &&
      ((this._tableRef && this._selectedCount > 0) || item !== null)
    );
  }
  //#endregion

  //#region CONSTRUCTOR and ng*
  constructor() {
    if (this._userService.CurrentUser.resourceID) {
      this._md.Resources.GetGroupIDsOfUserResource(
        this._userService.CurrentUser.resourceID,
      )
        .pipe(
          tap((result) => (this._myResourceGroupIDs = result)),
          catchError((error) => {
            console.error(error);
            return EMPTY;
          }),
          takeUntil(this._destroy$),
        )
        .subscribe();
    }

    this.cstDataSource = new CstListViewDataSource<TaskViewModel>({
      sorting: {
        column: "taskNo",
        direction: "desc",
        enableServerSideSorting: true,
      },
      pagination: { pageSize: 50, offset: 0 },
    });

    this._loadMasterData();
  }

  ngOnDestroy(): void {
    this._disposables.dispose();
    this._destroy$.next();
    this._destroy$.complete();
  }
  //#endregion

  //#region CONTEXT MENU
  @ViewChild("contextMenu")
  private _contextMenuRef!: MaterialContextMenuComponent;

  public openContextMenu(event: { event: PointerEvent; data: TaskViewModel }) {
    this._buildContextMenu(event.data ? event.data : null);
    this._contextMenuRef.open(event.event);
  }

  private _buildContextMenu(item: TaskViewModel | null = null) {
    const selection = this._getSelectedTasks(false);
    if (item) selection.push(item);
    const selected = selection.length > 0 ? selection[0] : item;
    const multi = selection.length > 1;

    this._contextMenuRef.items = [];

    this._contextMenuRef.items.push(
      {
        id: "TaskInsert",
        label: "Neu",
        icon: "add",
        disabled: multi,
        callback: () => this.OnTaskInsert(),
      },
      { divider: true },
    );

    this._contextMenuRef.items.push(
      {
        id: "TaskChange",
        label: "Bearbeiten",
        icon: "edit",
        disabled: multi,
        callback: () => this.OnTaskUpdate(selected),
      },
      { divider: true },
    );

    this._contextMenuRef.items.push(
      {
        label: multi ? "Selektierte Aufgaben löschen" : "Löschen",
        icon: "delete",
        disabled: !this.canDelete(selected),
        callback: () => this.OnTasksDelete(selection),
      },
      { divider: true },
    );

    this._contextMenuRef.items.push({
      label: "Status",
      icon: "home",
      children: this.taskStatesForMultiSelect,
    });
  }
  //#endregion

  //#region PUBLIC HELPERS
  public reload() {
    this._reloadTasks();
  }

  public onRowClicked(row: TaskViewModel) {
    this.OnTaskUpdate(row);
  }
  //#endregion

  //#region PUBLIC HANDLERS
  public OnAnlageSelect(target: FormControl) {
    this._popupService
      .open(AnlageSearchComponent)
      .afterClosed()
      .pipe(
        filter((result: AnlageViewModel | null) => !!result),
        filter(
          (result: AnlageViewModel) =>
            !target.value.includes(result.anlageNrIntern),
        ),
        tap((result: AnlageViewModel) => {
          target.setValue([...target.value, result.anlageNrIntern]);
          target.markAsDirty();
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public OnFilterRemoveAnlageNr(anlNr: string, target: FormControl): void {
    if (!Array.isArray(target.value)) return;

    target.setValue(target.value.filter((v: string) => v !== anlNr));
    this._reloadTasks();
  }
  //#endregion

  //#region FIELDS
  private _destroy$ = new Subject<void>();
  public isLoading = false;
  public appointmentArrangement = AppointmentArrangement;
  private readonly _disposables = new CstDisposableContainer();
  private _myResourceGroupIDs: string[] = [];
  private _statusMetas: Array<TaskStatusMetaDto> | null = null;
  public exportMenuItems = [
    {
      label: "Aktuelle Ansicht nach Excel exportieren",
      icon: "fa fa-file-excel fa-fw",
      callback: this._OnTasksExportExcel.bind(this),
      iconType: IconType.FontAwesome,
    },
    {
      label: "Alle Aufgaben detailliert nach CSV exportieren",
      icon: "fa fa-file fa-fw",
      callback: this._OnTasksExportDetailCSV.bind(this),
      iconType: IconType.FontAwesome,
    },
  ];
  //#endregion

  //#region FILTER SETUP
  public taskStatesForMultiSelect = new Array<TaskStatesForMultiSelect>();

  @ViewChild("filterTemplateResources")
  filterTemplateResources!: TemplateRef<any>;
  @ViewChild("filterTemplateFacilityNo")
  filterTemplateFacilityNo!: TemplateRef<any>;
  @ViewChild("filterTemplateTaskStates")
  filterTemplateTaskStates!: TemplateRef<any>;
  @ViewChild("filterTemplateMisc") filterTemplateMisc!: TemplateRef<any>;
  @ViewChild("fb") filterBarRef!: CstFilterBarComponent;

  resources: Array<ResourceViewModel> = [];
  categories: Array<TaskCategoryDto> = [];
  taskStates: Array<EnumNameValue> = [];
  filterGroup: ICstFilterGroup<ITaskListFilter> | null = null;

  private _initializeFilter(): void {
    const keySavedFilter =
      this._userService.CurrentUser.mandantID + "_" + "TaskListFilter";

    this.filterGroup = this._filterBuilder
      .group<ITaskListFilter>({
        dateRangeStart: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_DateRangeStart:Range-Start`,
          initialValue: null,
          dataType: "date",
          formatValue: CstFilterFormats.date(this._locale),
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryDate,
          convertToQuery: CstFilterQueryFunctions.convertToQueryDate,
        },
        dateRangeEnd: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_DateRangeEnd:Range-End`,
          initialValue: null,
          dataType: "date:end_of_day",
          formatValue: CstFilterFormats.date(this._locale),
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryDate,
          convertToQuery: CstFilterQueryFunctions.convertToQueryDate,
          validator: (control) =>
            CustomValidators.dateGreaterThan(
              () => control.parent?.get("dateRangeStart")?.value,
            )(control),
        },
        appointmentArrangements: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_AppointmentArrangements:Appointment Arrangements`,
          initialValue: [],
          template: this.filterTemplateMisc,
          formatValue: this._enumService.appointmentArrangementToString,
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryArrayOfNumbers,
        },
        categories: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_Categories:Categories`,
          initialValue: [],
          template: this.filterTemplateMisc,
          formatValue: (value) =>
            this.categories?.find((x) => x.id === value)?.label ?? "Unbekannt",
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryArray,
        },
        states: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_States:States`,
          initialValue: 0,
          template: this.filterTemplateTaskStates,
          chipConfig: {
            transform: this._filterChipTaskStateTransform.bind(this),
            remove: this._filterChipTaskStateRemove.bind(this),
          },
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryArrayOfNumbers,
        },
        anlageNr: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_FacilityNumbers:Facilities`,
          initialValue: [],
          template: this.filterTemplateFacilityNo,
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryArray,
        },
        taskNo: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_TaskNo:Task No.`,
          initialValue: "",
        },
        taskText: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_TaskText:Task Text`,
          initialValue: "",
        },
        workLocation: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_WorkLocation:Work Location`,
          initialValue: null,
        },
        claimByResourceIds: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_ClaimByResourceIds:Claimed`,
          initialValue: [],
          template: this.filterTemplateResources,
          formatValue: (value) => this._resolveResourceName(value),
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryArray,
        },
        resourceID: {
          label: $localize`:@@PlanungAPI_Tasks_Filter_ResourceID:Assigned`,
          initialValue: [],
          template: this.filterTemplateResources,
          formatValue: (value) => this._resolveResourceName(value),
          parseFromQuery: CstFilterQueryFunctions.parseFromQueryArray,
        },
        onlyWithUnreadNotes: !this._isSaasInstance
          ? {
              label: $localize`:@@PlanungAPI_Tasks_Filter_OnlyWithUnreadNotes:Only with unread Notes`,
              initialValue: false,
            }
          : undefined,
      })
      .withDialogDimensions({ maxWidth: "700px" })
      .withQuickFilters(this._quickFilters)
      .withStorageKey(keySavedFilter)
      .usingQueryParams(this._router)
      .build();

    this._disposables.register(this.filterGroup);
  }

  private get _quickFilters() {
    const user = this._userService.CurrentUser;

    const filters = [
      {
        label: $localize`:@@Planing_Overview_Tasks_FilterOpenTasks:All open Tasks`,
        title: $localize`:@@Planing_Overview_Tasks_FilterOpenTasks_Title:Set a quick filter to show all open tasks.`,
        value: {
          states:
            TaskStatus.Preparation +
            TaskStatus.Open +
            TaskStatus.Processing +
            TaskStatus.ToDo +
            TaskStatus.OnHold,
        },
      },
      {
        label: $localize`:@@Planing_Overview_Tasks_FilterCompletedTasks:All completed Tasks`,
        title: $localize`:@@Planing_Overview_Tasks_FilterCompletedTasks_Title:Set a quick filter to show all completed tasks.`,
        value: {
          states:
            TaskStatus.ClosedDontInvoice +
            TaskStatus.ClosedDoInvoice +
            TaskStatus.ClosedAlreadyInvoiced,
        },
      },
      user.resourceID
        ? {
            label: $localize`:@@Planing_Overview_Tasks_Filter_MyTasks:My Tasks`,
            title: $localize`:@@Planing_Overview_Tasks_Filter_MyTasks_Title:Set a quick filter to show all Tasks I am currently working on.`,
            value: {
              claimByResourceIds: [user.resourceID],
            },
          }
        : undefined,
      user.resourceID
        ? {
            label: $localize`:@@Planing_Overview_Tasks_Filter_PotentialTasks:Potential Tasks`,
            title: $localize`:@@Planing_Overview_Tasks_Filter_PotentialTasks_Title:Set a quick filter to show all Tasks that are assigned to me or a group I'm in.`,
            value: {
              resourceID: [user.resourceID, ...this._getResourceGroupIDsOfMe()],
            },
          }
        : undefined,
      {
        breakBefore: true,
        label: $localize`:@@Planing_Overview_Tasks_Filter_Today:Today`,
        title: $localize`:@@Planing_Overview_Tasks_Filter_Today_Title:Set a quick filter to show all tasks from today.`,
        value: () => this._formatDateRange(this._dts.getRangeOfDayForDate()),
      },
      {
        label: $localize`:@@Planing_Overview_Tasks_Filter_ThisWeek:This Week`,
        title: $localize`:@@Planing_Overview_Tasks_Filter_ThisWeek_Title:Set a quick filter to show all tasks from this week.`,
        value: () => this._formatDateRange(this._dts.getRangeOfWeekForDate()),
      },
      {
        label: $localize`:@@Planing_Overview_Tasks_Filter_ThisMonth:This Month`,
        title: $localize`:@@Planing_Overview_Tasks_Filter_ThisMonth_Title:Set a quick filter to show all tasks from this month.`,
        value: () => this._formatDateRange(this._dts.getRangeOfMonthForDate()),
      },
      {
        label: $localize`:@@Planing_Overview_Tasks_Filter_ThisYear:This Year`,
        title: $localize`:@@Planing_Overview_Tasks_Filter_ThisYear_Title:Set a quick filter to show all tasks from this year.`,
        value: () => this._formatDateRange(this._dts.getRangeOfYearForDate()),
      },
    ];

    return filters.filter((f) => !!f);
  }
  //#endregion

  //#region DATA FETCHING
  private _loadMasterData() {
    this.isLoading = true;

    forkJoin({
      resources: this._loadResources(),
      categories: this._loadCategories(),
      states: this._md.Tasks.GetTaskStates(),
      metas: this._md.Tasks.GetTaskStateMetas(),
    })
      .pipe(
        tap(({ resources, categories, states, metas }) => {
          this.resources = resources ?? [];
          this.categories = categories ?? [];
          this._statusMetas = metas.statusMetaInfos ?? [];

          this.taskStates =
            states.namesValues
              ?.filter(
                (x) =>
                  (x.value ?? 0) > 0 &&
                  (this._isSaasInstance ||
                    x.value !== TaskStatus.ClosedAlreadyInvoiced),
              )
              .map((x) => new EnumNameValue(x)) ?? [];

          this.taskStatesForMultiSelect = this.taskStates.map(
            (status) =>
              new TaskStatesForMultiSelect(
                status.value,
                status.name,
                this.OnTaskStatusSelect.bind(this, status.value),
              ),
          );
        }),
        switchMap(() => this._route.fragment.pipe(take(1))),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: (fragment) => {
          this._handleFragment(fragment);

          if (!this.filterGroup) this._initializeFilter();

          this.cstDataSource?.registerFetchDataCallback(
            this._loadTasksAsync.bind(this),
          );
          this.cstDataSource?.refresh();

          this.isLoading = false;
          this._cd.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;
          this._handleErrorPushNotification(err);
        },
      });
  }

  private _loadResources() {
    return forkJoin({
      types: this._md.Resources.GetTypes(),
      data: this._md.Resources.GetResources(
        false,
        false,
        ArchivedFilter.AllEntries,
      ),
    }).pipe(
      map(({ types, data }) => {
        return data.resources?.map((resource) => {
          const vm = new ResourceViewModel(resource);

          const matchedType = types.namesValues?.find(
            (x) => x.value === vm.type,
          );
          vm.typeName = matchedType?.name ?? "Unknown Type";

          return vm;
        });
      }),
      catchError((error) => {
        console.error("Failed to load resources:", error);
        return of([]);
      }),
    );
  }

  private _loadCategories() {
    return this._categoriesClient.getList(true, 0, 100000).pipe(
      map((res) => res.data),
      catchError((error) => {
        this._alrt.showSnackbar("Error loading categories:", error);
        return of([]);
      }),
    );
  }

  private _reloadTasks() {
    this._resetSelection();
    if (this.cstDataSource?.state.pagination) {
      this.cstDataSource.state.pagination.offset = 0;
    }
    this.cstDataSource?.refresh();
    this._cd.detectChanges();
  }

  public _loadTasksAsync(): Observable<{
    data: TaskViewModel[];
    total: number;
  }> {
    this.isLoading = true;

    return this._tasksClient.getAll(...this._getAllTasksConfig).pipe(
      map((result) => {
        const tasks =
          result.data?.map((task) => this._mapToViewModel(task)) ?? [];
        return {
          data: tasks,
          total: result.total ?? tasks.length,
        };
      }),
      catchError((err) => {
        this._handleErrorPushNotification(err);
        return of({ data: [], total: 0 });
      }),
      finalize(() => (this.isLoading = false)),
      takeUntil(this._destroy$),
    );
  }
  //#endregion

  //#region PRIVATE HELPERS
  private _getSelectedTasks(allIfEmpty: boolean): TaskViewModel[] {
    if (this._selected.length === 0 && allIfEmpty)
      return this.cstDataSource?.getAllRows ?? [];
    return this._selected;
  }

  private _resetSelection() {
    if (this._tableRef) this._selection.clear();
  }

  private _mapToViewModel(task: any): TaskViewModel {
    const vm = new TaskViewModel(task);

    vm.assignedToResource = this._resolveResourceName(vm.assignedToResourceID);
    vm.claimResource = this._resolveResourceName(vm.claimResourceID);

    vm.stateName = this._tryFindStateName(vm.state);
    vm.stateMeta =
      this._statusMetas?.find((sm) => sm.state === vm.state) ?? null;

    return vm;
  }

  private _resolveResourceName(value: any): string {
    if (!value || value === "null") {
      return "-- keiner --";
    }

    const match = this.resources?.find((x) => x.id === value);

    return match ? match.name : "Unbekannt";
  }

  private _formatDateRange(range: { begin: Date; end: Date }) {
    return { dateRangeStart: range.begin, dateRangeEnd: range.end };
  }

  private _handleFragment(fragment: string | null): void {
    if (!fragment) return;

    const action = fragment.toLowerCase();
    if (action === "neu") {
      this.OnTaskInsert();
    } else if (action === "import") {
      this.OnTaskImport();
    }
  }

  private _setUrlFragment(fragment?: string | null) {
    this._router.navigate(["."], {
      relativeTo: this._route,
      fragment: fragment ?? undefined,
      queryParamsHandling: "preserve",
    });
  }

  private _tryFindResourceName(resID: string): string {
    const resource = this.resources.find((r) => r.id === resID);
    if (!resource) return resID;

    return resource.archived ? `${resource.name} (Archiviert)` : resource.name;
  }

  private _tryFindStateName(stateID: number): string {
    const state = this.taskStates.find((ele) => ele.value === stateID);
    return state ? state.name : stateID.toString();
  }

  private get _getAllTasksConfig(): Parameters<TasksClientProvider["getAll"]> {
    if (!this.filterGroup) {
      throw new Error("Filter group is not initialized");
    }

    return [
      this.filterGroup.value.resourceID || [],
      this.filterGroup.value.dateRangeStart,
      this.filterGroup.value.dateRangeEnd,
      this.filterGroup.value.anlageNr || [],
      this.filterGroup.value.states,
      this.filterGroup.value.taskNo,
      false,
      false,
      false,
      this.filterGroup.value.dateRangesFilterOnlyToDoDate ?? false,
      this.filterGroup.value.anlageGroup || [],
      this.filterGroup.value.taskText,
      this.filterGroup.value.claimByResourceIds || [],
      this.filterGroup.value.categories || [],
      [],
      this.filterGroup.value.appointmentArrangements || [],
      this.filterGroup.value.onlyWithUnreadNotes,
      this.filterGroup.value.workLocation,
      this.cstDataSource?.state.pagination?.offset,
      this.cstDataSource?.state.pagination?.pageSize,
      `${this.cstDataSource?.state?.sorting?.column} ${this.cstDataSource?.state?.sorting?.direction}`,
    ];
  }

  private _filterChipTaskStateTransform(
    value: number,
  ): { label: string; value: any }[] {
    if (value === 0) {
      return [];
    }

    const chips: { label: string; value: any }[] = [];
    for (const state of this.taskStates) {
      if (value & state.value) {
        chips.push({ label: state.name, value: state.value });
      }
    }
    return chips;
  }

  private _getResourceGroupIDsOfMe() {
    return this._myResourceGroupIDs;
  }
  //#endregion

  //#region PRIVATE HANDLERS
  private _handleErrorPushNotification(error: any) {
    console.error(error);
    // Nachdem ein Fehler passiert is, soll auf jeden Fall das isLoading zurückgesetzt werden
    this.isLoading = false;
    this._alrt.showSnackbarError("Error: " + error);
  }

  private _filterChipTaskStateRemove(value: number, filter: ICstFilterItem) {
    let tmp = filter.value;
    tmp &= ~value;
    filter.setValue(tmp);
    this._reloadTasks();
  }
  //#endregion

  //#region INSERT TASK
  public OnTaskInsert() {
    this._setUrlFragment("neu");
    this.isLoading = true;

    this._popupService
      .open(TaskInsertComponent)
      .afterClosed()
      .pipe(
        switchMap((result: TaskInsertDto) => {
          this._setUrlFragment(null);

          if (!result || !(result instanceof TaskInsertDto)) {
            this.isLoading = false;
            return EMPTY;
          }

          return this._tasksClient.postSingle(result).pipe(
            map((postResult: TaskDetailDto) => {
              const taskVm = new TaskViewModel(postResult);
              taskVm.assignedToResource = this._tryFindResourceName(
                taskVm.assignedToResourceID,
              );
              taskVm.stateName = this._tryFindStateName(taskVm.state);

              this._router.navigate(["./", taskVm.taskNo], {
                relativeTo: this._route,
              });

              return { success: true, message: null };
            }),
            catchError((error) =>
              of({
                success: false,
                message: error.message || "Fehler beim Erstellen der Aufgabe",
              }),
            ),
          );
        }),
        takeUntil(this._destroy$),
        finalize(() => this._reloadTasks()),
      )
      .subscribe((res) => {
        if (res.success) {
          this._handleChangeTaskStatusResults();
        } else {
          this._handleChangeTaskStatusResults(res.message);
        }
      });
  }
  //#endregion

  //#region EDIT TASK
  public OnTaskUpdate(task: TaskViewModel | null) {
    if (!task) {
      return;
    }
    this._nav.navigateTo_Planung_Tasks(task.taskNo);
  }
  //#endregion

  //#region TASK STATUS
  public OnTaskStatusSelect(newState: any, task: TaskViewModel) {
    const sel = this._getSelectedTasks(false);
    if (sel.length === 0 && task) sel.push(task);
    if (sel.length === 0) return;

    // else return;

    this.isLoading = true;

    const taskRequests$ = sel.map((task) => {
      return this._tasksClient.getSingle(task.id).pipe(
        switchMap((taskDetail) => {
          const command = new TaskUpdateCommand(taskDetail);
          command.state = newState;
          command.lastChanged = new Date();

          return this._tasksClient.update(command).pipe(
            map(() => ({ success: true, message: null })),
            catchError((error) => {
              const msg =
                error.message || "Fehler beim Ändern des Aufgaben-Status";
              return of({
                success: false,
                message: `Aufgabe ${taskDetail.taskNo}: ${msg}`,
              });
            }),
          );
        }),
        catchError(() =>
          of({
            success: false,
            message: `Aufgabe ID ${task.id}: Konnte nicht geladen werden.`,
          }),
        ),
      );
    });

    forkJoin(taskRequests$)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this._reloadTasks()),
      )
      .subscribe((results) => {
        const errorMessages = results
          .filter((r) => !r.success && !!r.message)
          .map((r) => r.message as string);

        if (errorMessages.length === 0) {
          this._handleChangeTaskStatusResults();
        } else {
          this._handleChangeTaskStatusResults(errorMessages);
        }
      });
  }

  private _handleChangeTaskStatusResults(errorMessage: string | string[] = "") {
    if (errorMessage.length === 0) {
      //this._alrt.showSnackbar("Operation successful");
      return;
    }

    if (typeof errorMessage === "string") {
      this._handleErrorPushNotification(errorMessage);
      return;
    }

    if (Array.isArray(errorMessage)) {
      this._handleErrorPushNotification(errorMessage.join("\n"));
      return;
    }
  }
  //#endregion

  //#region IMPORT TASK
  public OnTaskImport() {
    this._setUrlFragment("import");
    this._popupService
      .open(PlanungApiTaskImportCsvComponent)
      .afterClosed()
      .pipe(
        map((res) => (res ? res : null)),
        finalize(() => {
          this._setUrlFragment(null);
          this._reloadTasks();
        }),
        takeUntil(this._destroy$),
        catchError((error) => {
          this._handleChangeTaskStatusResults(error.message);
          return EMPTY;
        }),
      )
      .subscribe();
  }
  //#endregion

  //#region DELETE TASK
  public OnTasksDelete(target?: TaskViewModel | TaskViewModel[]) {
    const targets: TaskViewModel[] = Array.isArray(target)
      ? target
      : target
        ? [target]
        : [...this._selected];

    if (!this.canDelete || targets.length === 0) return;
    const isSingle = targets.length === 1;

    const message = isSingle
      ? $localize`:@@Dialogs_PlanungAPI_Tasks_ConfirmDeletion_Prompt_Single:Please specify a reason why you are deleting the task:`
      : $localize`:@@Dialogs_PlanungAPI_Tasks_ConfirmDeletion_Prompt_Multi:Please specify a reason why you are deleting the tasks:`;

    const title = isSingle
      ? $localize`:@@Dialogs_PlanungAPI_Tasks_ConfirmDeletion_Title_Single:Delete the task?`
      : $localize`:@@Dialogs_PlanungAPI_Tasks_ConfirmDeletion_Title_Multi:Delete the selected tasks?`;

    this._dialog
      .AskYesCancelPromptString(message, title)
      .pipe(
        exhaustMap((result: string | CstDialogResult) => {
          if (result === CstDialogResult.Cancel || typeof result !== "string") {
            return EMPTY;
          }

          const reason = result.trim();
          if (reason.length < 3) {
            return EMPTY;
          }

          this.isLoading = true;
          // const reason = result as string;
          const request$ = isSingle
            ? this._tasksClient.deleteSingle(targets[0].id, reason)
            : this._tasksClient.deleteMulti(
                new TasksDeleteMultiCommand({
                  taskIDs: targets.map((t) => t.id),
                  archivedReason: reason,
                }),
              );

          return request$.pipe(
            tap(() => {
              this._alrt.showSnackbar(
                $localize`:@@PlanungApiTasks_DeletionSuccessful: Deletion successful`,
                {
                  duration: "short",
                  type: "info",
                },
              );
              this._reloadTasks();
            }),
            catchError((error) => {
              console.error(error);
              this._alrt.showSnackbar(
                $localize`:@@PlanungApiTasks_DeletionFailed: Deletion failed`,
                {
                  type: "error",
                },
              );
              return EMPTY;
            }),
            finalize(() => (this.isLoading = false)),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  private _OnTasksExportExcel() {
    const page = this.cstDataSource?.page;

    if (!page || page.length === 0) {
      this._alrt.showSnackbar(
        $localize`:@@PlanungApiTasks_NoExportRows:Current view doesn't contain any rows to export`,
      );
      return;
    }

    const exportColumns = this._buildColumns(ListColumnDisplay.Export);
    const headers = exportColumns.map((c) => c.header ?? c.field);

    const data = page.map((item) => {
      return exportColumns.map((col) => {
        let value = (item as any)[col.field];

        if (col.converter) {
          value = col.converter(value, item, col);
        }

        return value ?? "";
      });
    });

    this._excel.saveExcel(data, "aufgaben", headers);
  }

  private _OnTasksExportDetailCSV() {
    const command = new TasksExportCsvCommand({
      taskIDs: this._getSelectedTasks(true).map((t) => t.id),
    });
    this._tasksClient
      .exportTasksCSV(command)
      .subscribe((result: FileResponse) => {
        if (!result.fileName) {
          result.fileName = "aufgaben.csv";
        }
        FileHelper.DownloadFile(result);
      });
  }
  //#endregion

  //#region COLUMN DEFS
  private _buildColumns(
    display: ListColumnDisplay,
  ): ICstListViewColumn<TaskViewModel>[] {
    const columns: ICstListViewColumn<TaskViewModel>[] = [
      {
        field: "appointmentArrangement",
        header: "",
        width: "150px",
        displayIn: ListColumnDisplay.Web,
        serverSortField: "AppointmentArrangement",
      },
      {
        field: "taskNo",
        header: "#",
        width: "100px",
        serverSortField: "ClusteredIdentity",
      },
      {
        field: "category",
        header: $localize`:@@PlanungApiTasksTaskCategory:Category`,
        width: "120px",
        serverSortField: "Category.Label",
        escapeHtml: true,
      },
      {
        field: "stateName",
        header: $localize`:@@status:Status`,
        width: "120px",
        serverSortField: "State",
      },
      {
        field: "label",
        header: $localize`:@@task:Task`,
        serverSortField: "Label",
      },
      {
        field: "taskLocation",
        header: $localize`:@@PlanungApiTasksTaskLocation:Task Location`,
        serverSortField:
          "(WorkLocation.Name + WorkLocation.Street + WorkLocation.StreetNo + WorkLocation.PostalCode + WorkLocation.City)",
      },
      {
        field: "DateRangesActiveDisplay",
        header: $localize`:Planung aktiv@@PlanungApiTasksPlanningActive:Planning Active`,
        serverSortField: "DateRangesActive",
      },
      {
        field: "plannedFor",
        header: $localize`:@@PlannungApiTasksPlannedFor:Planned For`,
        template: undefined,
        serverSortField: "TodoDate",
      },
      {
        field: "dueDate",
        header: $localize`:@@PlanungAPI_Tasks_DueDate:Due Date`,
        format: "date",
        serverSortField: "DueDate",
      },
      {
        field: "claimResource",
        header: $localize`:@@GetsProcessedBy:Claimed By`,
        serverSortField: "ClaimedBy",
      },
      {
        field: "assignedToResource",
        header: $localize`:@@MeantFor:Assigned To`,
        serverSortField: "AssignedTo",
      },
      ...(this._isSaasInstance
        ? []
        : [
            {
              field: "sammelNr",
              header: $localize`:@@List_Header_SammelNr:Collection-No.`,
              serverSortField: "TaskBeleg.ReceiptID",
            },
          ]),
      {
        field: "anlageNrIntern",
        header: $localize`:@@List_Header_AnlageNr:Facility No.`,
        serverSortField: "AnlageNrIntern",
      },
      {
        field: "changed",
        header: $localize`:@@PlanungAPI_Tasks_Changed:Changed`,
        format: "date",
        serverSortField: "Changed",
      },
      {
        field: "inserted",
        header: $localize`:@@PlanungAPI_Tasks_Inserted:Inserted`,
        format: "date",
        serverSortField: "Inserted",
      },
      {
        field: "appointmentArrangement",
        header: $localize`:@@PlanungAPI_Tasks_AppointmentArrangement:Appointment Arrangement`,
        width: "450px",
        displayIn: ListColumnDisplay.Export,
        converter: (value: AppointmentArrangement) => {
          switch (value) {
            case AppointmentArrangement.None:
              return $localize`:@@AppointmentArrangement_None:No appointment required`;

            case AppointmentArrangement.Required:
              return $localize`:@@AppointmentArrangement_Required_Tooltip:Appointment must be made`;

            case AppointmentArrangement.Scheduled:
              return $localize`:@@AppointmentArrangement_Scheduled_Tooltip:Appointment scheduled`;

            default:
              return `${value}`;
          }
        },
        serverSortField: "AppointmentArrangement",
      },
      {
        field: "hasUnreadNotes",
        header: $localize`:@@PlanungApi_Tasks_HasUnreadNotes:Unread Notes`,
        converter: (value: boolean) => this._yesNoPipe.transform(value),
        displayIn: ListColumnDisplay.Export,
      },
    ];
    return columns.filter(
      (col) => col.displayIn === undefined || col.displayIn === display,
    );
  }
  //#endregion
}
